import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Tabs,
  Tab,
  IconButton,
  Typography,
  Alert,
  Card,
  CardContent,
  CardActions,
  Grid,
  Chip,
  TextField,
  InputAdornment,
} from "@mui/material";
import {
  Close as CloseIcon,
  Add as AddIcon,
  LibraryAdd as LibraryAddIcon,
  Save as SaveIcon,
  CheckBox as CheckBoxIcon,
  Image as ImageIcon,
  Comment as CommentIcon,
  ManageSearch as ManageSearchIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import { addDoc, onSnapshot, doc, updateDoc, deleteDoc } from "firebase/firestore";
import { propertyCollection, propertyDoc } from "../utils/firestorePaths";
import ChecklistViewer from "./ChecklistViewer";
import ChecklistEditor from "./ChecklistEditor";
import { createChecklist } from "../utils/checklistUtils";

/**
 * ChecklistModal - Main modal for managing checklists with template support
 * @param {boolean} open - Modal open state
 * @param {Function} onClose - Close handler
 * @param {Array} checklists - Array of checklists currently on this dispatch item
 * @param {Function} onSave - Save handler
 * @param {boolean} readOnly - Read-only mode
 * @param {string} clientId - Owning client ID for loading templates
 * @param {string} selectedProperty - Property ID for loading templates
 */
const ChecklistModal = ({
  open,
  onClose,
  checklists = [],
  onSave,
  readOnly = false,
  clientId,
  selectedProperty
}) => {
  const [localChecklists, setLocalChecklists] = useState([]);
  const [selectedTab, setSelectedTab] = useState(0);
  const [editingChecklistId, setEditingChecklistId] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [viewMode, setViewMode] = useState("checklists"); // "checklists" or "templates"
  const [searchQuery, setSearchQuery] = useState("");

  // Load checklist templates from Firestore
  useEffect(() => {
    if (!selectedProperty || !open) return;

    const templatesRef = propertyCollection(
      clientId,
      selectedProperty,
      "checklistTemplates"
    );

    const unsubscribe = onSnapshot(templatesRef, (snapshot) => {
      const loadedTemplates = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setTemplates(loadedTemplates);
    });

    return () => unsubscribe();
  }, [selectedProperty, open]);

  // Initialize local state when modal opens
  useEffect(() => {
    if (open) {
      setLocalChecklists(JSON.parse(JSON.stringify(checklists || [])));
      setSelectedTab(0);
      setEditingChecklistId(null);
      setHasChanges(false);
      // If no checklists, show templates view
      if (!checklists || checklists.length === 0) {
        setViewMode("templates");
      } else {
        setViewMode("checklists");
      }
    }
  }, [open, checklists]);

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    // If selecting "All Checklists" tab (always last tab)
    if (newValue === localChecklists.length) {
      setViewMode("templates");
    } else {
      setViewMode("checklists");
      setSelectedTab(newValue);
      setEditingChecklistId(null);
    }
  };

  // Handle add new blank checklist
  const handleAddChecklist = () => {
    const newChecklist = createChecklist(`Checklist ${localChecklists.length + 1}`);
    const updatedChecklists = [...localChecklists, newChecklist];
    setLocalChecklists(updatedChecklists);
    setSelectedTab(updatedChecklists.length - 1);
    setEditingChecklistId(newChecklist.id);
    setViewMode("checklists");
    setHasChanges(true);
  };

  // Handle add from template
  const handleAddFromTemplate = (template) => {
    // Create a copy of the template with new ID and reset completion status
    const newChecklist = {
      ...JSON.parse(JSON.stringify(template)),
      id: `checklist_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      templateId: template.id, // Track which template this came from
      createdAt: new Date().toISOString(),
      lastModified: new Date().toISOString(),
      items: template.items.map(item => ({
        ...item,
        id: `checklist_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        completed: false,
        imageUrl: null,
        imagePath: null,
        comment: "",
      })),
    };
    const updatedChecklists = [...localChecklists, newChecklist];
    setLocalChecklists(updatedChecklists);
    setSelectedTab(updatedChecklists.length - 1);
    setViewMode("checklists");
    setHasChanges(true);
  };

  // Handle delete checklist
  const handleDeleteChecklist = (checklistId) => {
    const updatedChecklists = localChecklists.filter((cl) => cl.id !== checklistId);
    setLocalChecklists(updatedChecklists);
    if (selectedTab >= updatedChecklists.length) {
      setSelectedTab(Math.max(0, updatedChecklists.length - 1));
    }
    setHasChanges(true);
  };

  // Handle checklist update
  const handleChecklistUpdate = (checklistId, updatedChecklist) => {
    const updatedChecklists = localChecklists.map((cl) =>
      cl.id === checklistId ? { ...updatedChecklist, lastModified: new Date().toISOString() } : cl
    );
    setLocalChecklists(updatedChecklists);
    setHasChanges(true);
  };

  // Handle save current checklist as template
  const handleSaveAsTemplate = async () => {
    const currentChecklist = localChecklists[selectedTab];
    if (!currentChecklist || !selectedProperty) return;

    try {
      // Create template without completion data
      const templateData = {
        name: currentChecklist.name,
        items: currentChecklist.items.map(item => ({
          id: item.id,
          type: item.type,
          label: item.label,
          createdAt: item.createdAt,
        })),
        createdAt: new Date().toISOString(),
        lastModified: new Date().toISOString(),
      };

      const templatesRef = propertyCollection(
        clientId,
        selectedProperty,
        "checklistTemplates"
      );

      // Check if this checklist was based on a template - update it
      if (currentChecklist.templateId) {
        const templateDoc = doc(templatesRef, currentChecklist.templateId);
        await updateDoc(templateDoc, {
          ...templateData,
          lastModified: new Date().toISOString(),
        });
        alert("Template updated successfully!");
      } else {
        // Create new template
        await addDoc(templatesRef, templateData);
        alert("Template saved successfully!");
      }
    } catch (error) {
      console.error("Error saving template:", error);
      alert("Failed to save template. Please try again.");
    }
  };

  // Handle delete template
  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm("Are you sure you want to delete this template?")) return;

    try {
      const templateDoc = propertyDoc(
        clientId,
        selectedProperty,
        "checklistTemplates",
        templateId
      );
      await deleteDoc(templateDoc);
    } catch (error) {
      console.error("Error deleting template:", error);
      alert("Failed to delete template. Please try again.");
    }
  };

  // Handle save
  const handleSave = () => {
    onSave(localChecklists);
    onClose();
  };

  // Handle cancel
  const handleCancel = () => {
    if (hasChanges) {
      if (window.confirm("You have unsaved changes. Are you sure you want to close?")) {
        onClose();
      }
    } else {
      onClose();
    }
  };

  // Get icon for item type
  const getItemTypeIcon = (type) => {
    switch (type) {
      case "checkbox":
        return <CheckBoxIcon fontSize="small" />;
      case "image_required":
        return <ImageIcon fontSize="small" />;
      case "comment_required":
        return <CommentIcon fontSize="small" />;
      default:
        return <CheckBoxIcon fontSize="small" />;
    }
  };

  // Get current checklist
  const currentChecklist = localChecklists[selectedTab];

  // Render template selection view (when empty or on "All Checklists" tab)
  const renderTemplateSelection = () => {
    // Filter templates based on search query
    const filteredTemplates = templates.filter((template) =>
      template.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          {localChecklists.length === 0 ? "Select Checklist Templates" : "Available Templates"}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {localChecklists.length === 0
            ? "Choose one or more templates to add to this dispatch item"
            : "Browse and add templates to this dispatch item"}
        </Typography>

        {/* Search box */}
        <TextField
          fullWidth
          size="small"
          placeholder="Search templates..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 3 }}
        />

        {templates.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 5 }}>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              No templates available yet
            </Typography>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddChecklist}
              sx={{ mt: 2 }}
            >
              Create First Checklist
            </Button>
          </Box>
        ) : filteredTemplates.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 5 }}>
            <SearchIcon sx={{ fontSize: 48, color: "text.secondary", mb: 2 }} />
            <Typography variant="body1" color="text.secondary" gutterBottom>
              No templates match "{searchQuery}"
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Try a different search term
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {/* Option to create blank checklist */}
            <Grid item xs={12} sm={6} md={4}>
              <Card
                sx={{
                  height: "100%",
                  border: 2,
                  borderStyle: "dashed",
                  borderColor: "primary.main",
                  bgcolor: "background.paper",
                  cursor: "pointer",
                  transition: "all 0.2s",
                  "&:hover": {
                    bgcolor: "action.hover",
                    transform: "translateY(-4px)",
                    boxShadow: 3,
                  },
                }}
                onClick={handleAddChecklist}
              >
                <CardContent
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: 180,
                  }}
                >
                  <AddIcon sx={{ fontSize: 48, color: "primary.main", mb: 2 }} />
                  <Typography variant="h6" color="primary">
                    Create Blank
                  </Typography>
                  <Typography variant="body2" color="text.secondary" align="center">
                    Start from scratch
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Template cards */}
            {filteredTemplates.map((template) => (
              <Grid item xs={12} sm={6} md={4} key={template.id}>
                <Card
                  sx={{
                    height: "100%",
                    cursor: "pointer",
                    transition: "all 0.2s",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: 3,
                    },
                  }}
                >
                  <CardContent sx={{ pb: 1 }}>
                    <Typography variant="h6" gutterBottom noWrap>
                      {template.name}
                    </Typography>
                    <Chip
                      label={`${template.items?.length || 0} items`}
                      size="small"
                      sx={{ mb: 2 }}
                    />

                    {/* Preview of items */}
                    <Box sx={{ maxHeight: 100, overflow: "auto" }}>
                      {template.items?.slice(0, 3).map((item, idx) => (
                        <Box
                          key={idx}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mb: 0.5,
                          }}
                        >
                          {getItemTypeIcon(item.type)}
                          <Typography variant="caption" noWrap>
                            {item.label}
                          </Typography>
                        </Box>
                      ))}
                      {template.items?.length > 3 && (
                        <Typography variant="caption" color="text.secondary">
                          +{template.items.length - 3} more...
                        </Typography>
                      )}
                    </Box>
                  </CardContent>
                  <CardActions sx={{ justifyContent: "space-between" }}>
                    <Button
                      size="small"
                      startIcon={<LibraryAddIcon />}
                      onClick={() => handleAddFromTemplate(template)}
                    >
                      Add
                    </Button>
                    {!readOnly && (
                      <Button
                        size="small"
                        color="error"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteTemplate(template.id);
                        }}
                      >
                        Delete
                      </Button>
                    )}
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: "70vh",
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography variant="h6">Checklists</Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            {!readOnly && localChecklists.length > 0 && viewMode === "checklists" && (
              <Button
                variant="outlined"
                size="small"
                color="secondary"
                startIcon={<SaveIcon />}
                onClick={handleSaveAsTemplate}
              >
                Save as Template
              </Button>
            )}
            <IconButton size="small" onClick={handleCancel}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {localChecklists.length === 0 ? (
          // Show template selection when empty
          renderTemplateSelection()
        ) : (
          <>
            {/* Tabs for checklist navigation + All Checklists tab */}
            <Box sx={{ borderBottom: 1, borderColor: "divider", px: 2 }}>
              <Tabs
                value={viewMode === "templates" ? localChecklists.length : selectedTab}
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
              >
                {localChecklists.map((checklist, index) => (
                  <Tab
                    key={checklist.id}
                    label={checklist.name || `Checklist ${index + 1}`}
                  />
                ))}
                <Tab
                  icon={<ManageSearchIcon />}
                  iconPosition="start"
                  label="All Checklists"
                />
              </Tabs>
            </Box>

            {/* Content based on view mode */}
            <Box sx={{ p: 2 }}>
              {viewMode === "templates" ? (
                // Show template selection in "All Checklists" tab
                renderTemplateSelection()
              ) : currentChecklist ? (
                // Show current checklist
                <>
                  {editingChecklistId === currentChecklist.id ? (
                    <ChecklistEditor
                      checklist={currentChecklist}
                      onUpdate={(updatedChecklist) =>
                        handleChecklistUpdate(currentChecklist.id, updatedChecklist)
                      }
                      onDelete={() => handleDeleteChecklist(currentChecklist.id)}
                      onCancel={() => setEditingChecklistId(null)}
                      readOnly={readOnly}
                    />
                  ) : (
                    <ChecklistViewer
                      checklist={currentChecklist}
                      onEdit={() => setEditingChecklistId(currentChecklist.id)}
                      onUpdate={(updatedChecklist) =>
                        handleChecklistUpdate(currentChecklist.id, updatedChecklist)
                      }
                      readOnly={readOnly}
                    />
                  )}
                </>
              ) : null}
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        {hasChanges && (
          <Alert severity="info" sx={{ flex: 1, mr: 2 }}>
            You have unsaved changes
          </Alert>
        )}
        <Button onClick={handleCancel} color="inherit">
          {hasChanges ? "Cancel" : "Close"}
        </Button>
        {!readOnly && (
          <Button onClick={handleSave} variant="contained" disabled={!hasChanges}>
            Save Changes
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ChecklistModal;
